import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Form, Input, message } from 'antd';
import React, { useState } from 'react';
import axios from 'axios';
import store from 'store2';
import { useHistory } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { get } from 'lodash';

// Reconnected to constlending-api-next: authenticate via the GraphQL `login` mutation. `login` answers
// with one of three shapes and this screen owes a door to each — that is the whole flow:
//   requires2FA            → the operator has a phone; `verifyTwoFactor` trades the pending token + the
//                            SMS code for the session JWT.
//   requiresPhoneEnrollment→ the operator has NO phone yet; `startTwoFactorSetup` texts a code to the
//                            number they declare and `confirmTwoFactorSetup` persists it AND returns the
//                            session JWT — the session starts there, not at login.
//   neither                → the JWT is already a session.
// Either way the JWT is stored as `constitution-token` for the LendingBearer auth header, and it only
// gets stored after the decoded `isAdmin` check.
//
// PHONE ENROLLMENT ADDED (TL, 2026-09-12): the panel now walks the same recorrido as the investor
// dashboard (investor-dashboard-next/src/pages/Public/Auth/SetupTwoFactor), so an operator who has never
// registered a phone can get in by themselves instead of dead-ending on the sign-in form.
//
// REDRESSED, NOT REWIRED (TL, 2026-09-12): the screen wears the investor dashboard's language (Login.js
// owns the frame, login.css the styles) and every backend error surfaces verbatim as an antd `message`.

const LOGIN_MUTATION = `
  mutation Login($email: String!, $password: String!) {
    login(data: { email: $email, password: $password }) {
      token
      requires2FA
      requiresPhoneEnrollment
    }
  }
`;

// The public recovery door, shared with the investor dashboard. It answers `true` in EVERY branch on
// purpose — including an address with no account — so this screen can never be used to find out who has
// one. That is why the confirmation below is worded as a maybe and not as "sent".
const FORGOT_PASSWORD_MUTATION = `
  mutation ForgotPasswordRequest($email: String!) {
    forgotPasswordRequest(data: { email: $email })
  }
`;

const VERIFY_TWO_FACTOR_MUTATION = `
  mutation VerifyTwoFactor($code: String!, $token: String!) {
    verifyTwoFactor(data: { code: $code, token: $token })
  }
`;

const START_TWO_FACTOR_SETUP_MUTATION = `
  mutation StartTwoFactorSetup($phoneNumber: String!, $token: String!) {
    startTwoFactorSetup(data: { phoneNumber: $phoneNumber, token: $token })
  }
`;

const CONFIRM_TWO_FACTOR_SETUP_MUTATION = `
  mutation ConfirmTwoFactorSetup($code: String!, $phoneNumber: String!, $token: String!) {
    confirmTwoFactorSetup(data: { code: $code, phoneNumber: $phoneNumber, token: $token })
  }
`;

// The country code is FIXED, and that is a backend fact, not a UI shortcut: `user.phoneNumber` stores
// ten NATIONAL digits and never a country code (`toNationalDigits`), and every read rebuilds the number
// with `US_COUNTRY_CODE = '1'` (constlending-api-next/src/common/utils/phone.ts). A number enrolled from
// any other country would be stored stripped and then texted to +1<the same digits> on the next login.
// So the platform is single-country by construction and the dashboard's own fixed '+1'
// (SetupTwoFactor.tsx:19) matches what the backend can actually keep. Offering a picker here would
// promise a country the database cannot remember. Supporting a second one is a BACKEND change first.
const COUNTRY_CODE = '+1';

// Ported verbatim from investor-dashboard-next/src/@common/format/phone.format.ts (`toE164`), the shared
// helper both of its auth screens use — same ten-digit NANP rule, same '' for anything that cannot be a
// number. `startTwoFactorSetup`/`confirmTwoFactorSetup` reject anything that is not E.164.
function toE164(raw, countryCode) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';

  const codeDigits = countryCode.replace(/\D/g, '');
  let digits = trimmed.replace(/\D/g, '');

  if (digits.startsWith(codeDigits)) {
    digits = digits.slice(codeDigits.length);
  }

  if (digits.length < 10) return '';
  return `${countryCode}${digits.slice(0, 10)}`;
}

async function graphql(query, variables) {
  const { data: res } = await axios.post(process.env.REACT_APP_API_ENDPOINT, {
    query,
    variables,
  });

  if (res.errors && res.errors.length) {
    throw new Error(res.errors[0].message);
  }

  return res.data;
}

// The label sits above its box, as on the dashboard — antd's own label would put it beside it.
function Field({ label, children, ...item }) {
  return (
    <>
      <label className="admin-login__label" htmlFor={item.name}>
        {label}
      </label>
      <Form.Item {...item}>{children}</Form.Item>
    </>
  );
}

export function LoginForm() {
  const history = useHistory();
  // Token returned by `login` when the session is still owed a second factor; null = no step pending.
  const [pendingToken, setPendingToken] = useState(null);
  // Which door that token opens: 'verify' (the phone already exists) or 'enroll' (it does not). Two
  // values need no token: 'forgot' (asking for a recovery link) and 'forgot-sent' (already asked) —
  // `backToSignIn` clears them the same way, which is the whole reason they live in this switch.
  const [pendingStep, setPendingStep] = useState(null);
  // E.164 number the code was texted to. Empty while enrolling means "still asking for the number" —
  // the same switch the dashboard's SetupTwoFactor uses to pick its view.
  const [enrolledPhone, setEnrolledPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const backToSignIn = () => {
    setPendingToken(null);
    setPendingStep(null);
    setEnrolledPhone('');
  };

  // Answers whether the session was accepted: a rejected (non-admin) token leaves the operator on this
  // screen, so the caller knows whether it still owns a mounted component to unspin.
  const finishLogin = (token) => {
    const decoded = jwtDecode(token);
    if (!decoded.isAdmin) {
      message.error('This account is not an admin — access denied');
      return false;
    }

    store.set('constitution-token', token);
    store.set('constitution_email', get(decoded, 'email', ''));

    history.push('/');
    return true;
  };

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const data = await graphql(LOGIN_MUTATION, {
        email: values.email,
        password: values.password,
      });

      const login = data && data.login;
      if (!login || !login.token) {
        message.error('Login failed');
        setSubmitting(false);
        return;
      }
      if (login.requiresPhoneEnrollment) {
        setPendingToken(login.token);
        setPendingStep('enroll');
        setSubmitting(false);
        return;
      }
      if (login.requires2FA) {
        setPendingToken(login.token);
        setPendingStep('verify');
        message.info('Enter the verification code sent to your phone');
        setSubmitting(false);
        return;
      }

      // On a good login the panel takes over and this component is gone: touching its state then is the
      // React "update on an unmounted component" warning.
      if (!finishLogin(login.token)) setSubmitting(false);
    } catch (error) {
      message.error(error.message);
      setSubmitting(false);
    }
  };

  const onFinishTwoFactor = async (values) => {
    setSubmitting(true);
    try {
      const data = await graphql(VERIFY_TWO_FACTOR_MUTATION, {
        code: values.code,
        token: pendingToken,
      });

      const token = data && data.verifyTwoFactor;
      if (!token) {
        message.error('Verification failed');
        setSubmitting(false);
        return;
      }

      if (!finishLogin(token)) setSubmitting(false);
    } catch (error) {
      message.error(error.message);
      setSubmitting(false);
    }
  };

  const onFinishPhone = async (values) => {
    const to = toE164(values.phoneNumber, COUNTRY_CODE);
    if (!to) {
      message.error('Enter a valid 10-digit phone number');
      return;
    }

    setSubmitting(true);
    try {
      await graphql(START_TWO_FACTOR_SETUP_MUTATION, {
        phoneNumber: to,
        token: pendingToken,
      });

      setEnrolledPhone(to);
      message.info(`We texted a code to ${to}`);
      setSubmitting(false);
    } catch (error) {
      message.error(error.message);
      setSubmitting(false);
    }
  };

  const onFinishEnrollCode = async (values) => {
    setSubmitting(true);
    try {
      const data = await graphql(CONFIRM_TWO_FACTOR_SETUP_MUTATION, {
        code: values.code,
        phoneNumber: enrolledPhone,
        token: pendingToken,
      });

      // Confirm returns the SESSION token — the phone is now persisted as the operator's second factor.
      const token = data && data.confirmTwoFactorSetup;
      if (!token) {
        message.error('Verification failed');
        setSubmitting(false);
        return;
      }

      if (!finishLogin(token)) setSubmitting(false);
    } catch (error) {
      message.error(error.message);
      setSubmitting(false);
    }
  };

  // An admin created from the panel never gets a password typed for them: they arrive through this door
  // (the link in their email expires in an hour, and a lost one is asked for again right here).
  const onFinishForgot = async ({ email }) => {
    setSubmitting(true);
    try {
      await graphql(FORGOT_PASSWORD_MUTATION, { email: email.trim() });
      setPendingStep('forgot-sent');
    } catch (error) {
      message.error(error.message);
    }
    setSubmitting(false);
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const backButton = (
    <Button
      className="admin-login__back"
      type="link"
      htmlType="button"
      icon={<ArrowLeftOutlined />}
      onClick={backToSignIn}
    >
      Back to sign in
    </Button>
  );

  // The code box is the same in both doors — only what it is submitted to differs.
  const codeField = (
    <Field
      label="Verification code"
      name="code"
      rules={[
        {
          required: true,
          message: 'Required',
        },
      ]}
    >
      <Input
        id="code"
        autoFocus
        autoComplete="one-time-code"
        inputMode="numeric"
        placeholder="6-digit code"
      />
    </Field>
  );

  if (pendingStep === 'enroll' && !enrolledPhone) {
    return (
      <>
        {backButton}
        <p className="admin-login__eyebrow">Administration panel</p>
        <h1 className="admin-login__title">Enter your phone number</h1>
        <p className="admin-login__subtitle">
          This account has no second factor yet. We will text you a code to register this phone.
        </p>

        <Form
          key="phone"
          className="admin-login__form"
          name="phone"
          layout="vertical"
          onFinish={onFinishPhone}
          onFinishFailed={onFinishFailed}
        >
          <Field
            label="Phone number"
            name="phoneNumber"
            rules={[
              {
                required: true,
                message: 'Required',
              },
            ]}
          >
            <Input
              id="phoneNumber"
              autoFocus
              autoComplete="tel-national"
              inputMode="tel"
              placeholder="10-digit phone number"
              prefix={<span className="admin-login__country">{COUNTRY_CODE}</span>}
            />
          </Field>

          <Button
            className="admin-login__submit"
            type="primary"
            htmlType="submit"
            loading={submitting}
          >
            Send code
          </Button>
        </Form>
      </>
    );
  }

  if (pendingStep === 'enroll') {
    return (
      <>
        {backButton}
        <p className="admin-login__eyebrow">Administration panel</p>
        <h1 className="admin-login__title">Confirm your phone</h1>
        <p className="admin-login__subtitle">
          Enter the code we texted to {enrolledPhone} to finish registering it.
        </p>

        <Form
          key="enroll-code"
          className="admin-login__form"
          name="enroll-code"
          layout="vertical"
          onFinish={onFinishEnrollCode}
          onFinishFailed={onFinishFailed}
        >
          {codeField}

          <Button
            className="admin-login__submit"
            type="primary"
            htmlType="submit"
            loading={submitting}
          >
            Verify
          </Button>
        </Form>
      </>
    );
  }

  if (pendingStep === 'verify') {
    return (
      <>
        {backButton}
        <p className="admin-login__eyebrow">Administration panel</p>
        <h1 className="admin-login__title">Two-step verification</h1>
        <p className="admin-login__subtitle">Enter the code we texted to your phone.</p>

        <Form
          key="two-factor"
          className="admin-login__form"
          name="two-factor"
          layout="vertical"
          onFinish={onFinishTwoFactor}
          onFinishFailed={onFinishFailed}
        >
          {codeField}

          <Button
            className="admin-login__submit"
            type="primary"
            htmlType="submit"
            loading={submitting}
          >
            Verify
          </Button>
        </Form>
      </>
    );
  }

  if (pendingStep === 'forgot-sent') {
    return (
      <>
        {backButton}
        <p className="admin-login__eyebrow">Administration panel</p>
        <h1 className="admin-login__title">Check your email</h1>
        <p className="admin-login__subtitle">
          If that address belongs to an account, a link to set a new password is on its way. It is good
          for one hour. Come back here to sign in once you have chosen it.
        </p>
      </>
    );
  }

  if (pendingStep === 'forgot') {
    return (
      <>
        {backButton}
        <p className="admin-login__eyebrow">Administration panel</p>
        <h1 className="admin-login__title">Set your password</h1>
        <p className="admin-login__subtitle">
          Tell us your email address and we will send you a link to choose a password. Use this the
          first time too, if your account was just created for you.
        </p>

        <Form
          key="forgot"
          className="admin-login__form"
          name="forgot"
          layout="vertical"
          onFinish={onFinishForgot}
          onFinishFailed={onFinishFailed}
        >
          <Field
            label="Email address"
            name="email"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input
              id="email"
              autoFocus
              autoComplete="username"
              placeholder="Enter your email address"
            />
          </Field>

          <Button
            className="admin-login__submit"
            type="primary"
            htmlType="submit"
            loading={submitting}
          >
            Send me the link
          </Button>
        </Form>
      </>
    );
  }

  return (
    <>
      <p className="admin-login__eyebrow">Administration panel</p>
      <h1 className="admin-login__title">Sign in to continue</h1>
      <p className="admin-login__subtitle">Use your Constitution Lending operator account.</p>

      <Form
        key="basic"
        className="admin-login__form"
        name="basic"
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Field
          label="Email address"
          name="email"
          rules={[
            {
              required: true,
              message: 'Required',
            },
          ]}
        >
          <Input id="email" autoComplete="username" placeholder="Enter your email address" />
        </Field>

        <Field
          label="Password"
          name="password"
          rules={[
            {
              required: true,
              message: 'Required',
            },
          ]}
        >
          <Input.Password
            id="password"
            autoComplete="current-password"
            placeholder="Enter your password"
          />
        </Field>

        <Button
          className="admin-login__forgot"
          type="link"
          htmlType="button"
          onClick={() => setPendingStep('forgot')}
        >
          Forgot your password?
        </Button>

        <Button
          className="admin-login__submit"
          type="primary"
          htmlType="submit"
          loading={submitting}
        >
          Sign in
        </Button>
      </Form>
    </>
  );
}
